import { Undef } from 'types/UtilityTypes';

export type BidFlowCommittedContext = {
  emailKey: string;
  // Distinguishes same-thread email.id churn from a real thread switch.
  threadKey?: string;
  suggestionId?: number;
  // Set when a cross-thread stale suggestion carryover was dropped.
  // The same id may still arrive on the very next render before Kit clears
  // clickedSuggestion; this field lets us suppress it from being committed as
  // the new thread's first pick.
  staleSuggestionId?: number;
};

export type BidFlowContextResetAction =
  'apply-default' | 'commit-suggestion' | 'none';

export type BidFlowContextResetDecision = {
  action: BidFlowContextResetAction;
  next: BidFlowCommittedContext;
};

// Email change -> apply default. Null suggestion -> wait. First real id -> commit only.
// Suggestion A -> B -> apply default. Drop same suggestion id only on thread switch.
// staleSuggestionId guards the second render tick after a cross-thread drop so
// the stale id is not re-committed before Kit clears clickedSuggestion.
export function decideBidFlowContextReset(
  committed: Undef<BidFlowCommittedContext>,
  nextEmailKey: string,
  nextSuggestionId: Undef<number>,
  nextThreadKey?: Undef<string>
): BidFlowContextResetDecision {
  if (committed?.emailKey !== nextEmailKey) {
    const isSameThread =
      nextThreadKey != null &&
      nextThreadKey !== '' &&
      committed?.threadKey != null &&
      committed.threadKey !== '' &&
      nextThreadKey === committed.threadKey;

    // Same id across a *thread switch* is a leftover selection, not a new Kit pick.
    const isStaleSuggestionCarryover =
      !isSameThread &&
      nextSuggestionId != null &&
      committed?.suggestionId != null &&
      nextSuggestionId === committed.suggestionId;

    return {
      action: 'apply-default',
      next: {
        emailKey: nextEmailKey,
        threadKey: nextThreadKey,
        suggestionId: isStaleSuggestionCarryover ? undefined : nextSuggestionId,
        // Remember which id we dropped so the next render can suppress it.
        staleSuggestionId: isStaleSuggestionCarryover
          ? nextSuggestionId
          : undefined,
      },
    };
  }

  if (nextSuggestionId == null) {
    return {
      action: 'none',
      next: committed ?? {
        emailKey: nextEmailKey,
        threadKey: nextThreadKey,
      },
    };
  }

  // Suppress the stale id that was dropped in the previous pass. Kit has not
  // yet cleared clickedSuggestion so the same id arrives again here; treat it
  // the same as null (still waiting for the real hydration).
  if (nextSuggestionId === committed.staleSuggestionId) {
    return {
      action: 'none',
      next: committed,
    };
  }

  if (committed.suggestionId == null) {
    return {
      action: 'commit-suggestion',
      next: {
        emailKey: nextEmailKey,
        threadKey: nextThreadKey ?? committed.threadKey,
        suggestionId: nextSuggestionId,
      },
    };
  }

  if (committed.suggestionId !== nextSuggestionId) {
    return {
      action: 'apply-default',
      next: {
        emailKey: nextEmailKey,
        threadKey: nextThreadKey ?? committed.threadKey,
        suggestionId: nextSuggestionId,
      },
    };
  }

  return {
    action: 'none',
    next: committed,
  };
}
